import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import api from 'state/axios';
import { format, addDays, compareAsc } from "date-fns";
import toast from 'react-hot-toast';
import ExcelJS from "exceljs";
import { saveAs } from 'file-saver';
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography, 
    CircularProgress, 
    IconButton, 
    Tooltip, 
    Select, 
    MenuItem, 
    FormControl, 
    InputLabel,
    FormControlLabel, 
    Switch,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningIcon from '@mui/icons-material/Warning';
import DownloadIcon from '@mui/icons-material/Download';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import { 
    fetchFlights,
    fetchLegMembersByFlt, 
} from 'state/api';
import { logClientAction } from 'state/logger';
import { hasPermission } from 'state/checkPermission';
import ScrollToTop from 'components/ScrollToTop';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import FilterButton from 'components/FilterButton';
import NumberControl from 'components/NumberControl';
import DataGridComponent from 'components/DataGridComponent';
import CheckResultDialog from 'components/CheckResultDialog';
import FlightFilterDialog from './FlightFilterDialog';
import CrewListDialog from 'components/CrewListDialog';
import CrewCompositionList from "./CrewCompositionList";


const CrewComposition = () => {
    useAuthCheck();
    const user = useSelector((state) => state.user); 
    const timeMode = useSelector((state) => state.timemode);
    const crewRanksData = useSelector((state) => state.crewRanks);
    const params = useSelector((state) => state.params);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const canView = hasPermission(user?.permissions, "CrewComposition", "view", false);
    const canCheck = hasPermission(user?.permissions, "CrewComposition", "check", false);
      
    const { startDate, endDate } = useDateRangeContext();
    
    const scrollRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isGridStretched, setIsGridStretched] = useState(true);

    const [checksList, setChecksList] = useState([]);
    const [showCheckResultDialog, setShowCheckResultDialog] = useState(false);

    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState(null);
    const [openCrewListDialog, setOpenCrewListDialog] = useState(false);
    
    const [flights, setFlights] = useState([]); 
    const [flight, setFlight] = useState([]);
    const [crewList, setCrewList] = useState([]);
    const [jumpList, setJumpList] = useState([]);    

    const [checkType, setCheckType] = useState('');
    const [selectedRanks, setSelectedRanks] = useState([]);
    const [selectedACTypes, setSelectedACTypes] = useState([]);
    const [acTypes, setACTypes] = useState([]);
    const [aircrafts, setAircrafts] = useState([]);
    const [crewComplements, setCrewComplements] = useState([]);
    const [minFemaleQuantity, setMinFemaleQuantity] = useState(1);
    const [maxFemaleQuantity, setMaxFemaleQuantity] = useState(99);
        
    const checkTypes = [
        { value: '', label: 'None', italic: true },
        { value: 'gender', label: 'Gender' },
        { value: 'training', label: 'Training' },
        { value: 'crewCount', label: 'Crew Count' },
        { value: 'complement', label: 'Complement' },
    ];


    const fetchACTypes = useCallback(async () => {
        const res = await api.get('/actypes/active');
        return res.data;
    }, []);

    const fetchAircrafts = useCallback(async () => {
        const res = await api.get('/aircrafts/active');
        return res.data;
    }, []);

    const fetchCrewComplements = useCallback(async () => {
        const res = await api.get('/crewcomplements');
        return res.data;      
    }, []);

    const handleFlights = useCallback(async () => {
        if (!startDate || !endDate) return [];    
        try { 
            const result = await fetchFlights(addDays(startDate, -1), addDays(endDate, 1), true);

            if (result?.length > 0) {
                const filtered = result.filter(f => {
                    const flightDateToCompare = timeMode === 'UTC' 
                        ? f.flightDate 
                        : f.flightDateLocalBase;
                
                    return (
                        compareAsc(flightDateToCompare, startDate) >= 0 &&
                        compareAsc(flightDateToCompare, endDate) <= 0 &&
                        !f?.flightStatus?.toLowerCase().includes('cancelled') &&
                        !f?.flightStatus?.toLowerCase().includes('deleted')
                    );
                });
                
                return filtered;
            }
            return [];
        } catch (error) {
            toast.error(error?.message || "Failed to fetch flights!");
            return [];
        } 
    }, [timeMode, startDate, endDate]);
    
    useEffect(() => {
        const fetchData = async () => {
            try {
                setError(null);
                setLoading(true);
                
                const [
                    actypesResponse,
                    aircraftsResponse, 
                    crewComplementsResponse, 
                    flightsResponse,
                ] = await Promise.all([
                    fetchACTypes(),
                    fetchAircrafts(),        
                    fetchCrewComplements(),    
                    handleFlights(),        
                ]);                
                setACTypes(actypesResponse);
                setAircrafts(aircraftsResponse);
                setCrewComplements(crewComplementsResponse);
                setFlights(flightsResponse);
            } catch (error) {
                setError(error);
                toast.error(error.response?.data?.message || "Failed to fetch data!");
            } finally {
                setLoading(false);
            }
        };
    
        fetchData();      
    }, [fetchACTypes, 
        fetchAircrafts, 
        fetchCrewComplements,
        handleFlights,
    ]); 
             
    const filteredFlights = useMemo(() => {
        if (!flights || !Array.isArray(flights)) {
            return []; 
        }
    
        if (!filters) return flights; 
    
        return flights.filter((flight) => {
            const flightEtdStr = timeMode === 'UTC' ? flight.etd?.substring(0, 5) : flight.etdLocalBase?.substring(0, 5);
            const minEtd = filters.minEtd;
            const maxEtd = filters.maxEtd;

            if (minEtd && (!flightEtdStr || flightEtdStr < minEtd)) return false;
            if (maxEtd && (!flightEtdStr || flightEtdStr > maxEtd)) return false;

            return Object.entries(filters).every(([key, value]) => {
                if (!value) return true; // If no value, don't filter on this key
                                
                if (key === 'minEtd' || key === 'maxEtd') return true;
                
                if (key === 'flightDate') {
                    const formattedFlightDate = flight[key] ? format(new Date(flight[key]), 'yyyy-MM-dd') : null;
                    const formattedFilterDate = value ? format(new Date(value), 'yyyy-MM-dd') : null;
                    return formattedFlightDate === formattedFilterDate;
                }
                    
                if (key === 'flightNo') {
                    return flight[key] === Number(value); 
                }
                
                if (key === 'sector') {
                    return flight[key]?.toLowerCase().includes(value.toLowerCase());
                }

                if (key === 'acType' || key === 'flightReg') {
                    return Array.isArray(value) ? value.includes(flight[key]) : flight[key] === value;
                }

                if (key === 'isSufficientComplement') {
                    if (value === 'ALL') return true; 
                    if (value === 'SUFFICIENT') return flight[key] === true; 
                    if (value === 'INSUFFICIENT') return flight[key] === false; 
                }

                // For other fields, continue normal comparison
                return flight[key] === value;
            });
        });
    }, [timeMode, flights, filters]);      

    const hasCrewMembers = useMemo(() => {
        return filteredFlights?.some(flight => Array.isArray(flight?.crewMembers) && flight.crewMembers.length > 0);
    }, [filteredFlights]);

    
    const handleCheckErrors = useCallback(() => {
        try {
            if (!filteredFlights || filteredFlights.length === 0) {
                toast.warning("No flights available to check.");
                return;
            }
            
            const checks = [];

            const cabinCrewRanks = crewRanksData
                .filter(rank => rank.rankType === 'Cabin Crew')
                .map(rank => rank.rankCode);

            const getCrewComplement = (acType, flightReg, sector) => {
                const active = crewComplements.filter(cc => cc.isActive && cc.acTypeCode === acType);

                // Level 1: reg-specific + sector-specific
                const byRegAndSector = active.find(cc =>
                    cc.acRegs?.includes(flightReg) &&
                    cc.sectors?.length > 0 && cc.sectors.includes(sector)
                );
                if (byRegAndSector?.crewComplement?.length > 0) return byRegAndSector.crewComplement;

                // Level 2: reg-specific, any sector
                const byReg = active.find(cc =>
                    cc.acRegs?.includes(flightReg) &&
                    (!cc.sectors?.length)
                );
                if (byReg?.crewComplement?.length > 0) return byReg.crewComplement;

                // Level 3: type-level (all regs)
                const byType = active.find(cc => !cc.acRegs?.length);
                if (byType?.crewComplement?.length > 0) return byType.crewComplement;

                // Level 4: aircrafts by acReg
                const regInfo = aircrafts.find(a => a.acReg === flightReg);
                if (regInfo?.crewComplement?.length > 0) return regInfo.crewComplement;

                // Level 5: acTypes by acTypeCode
                const acInfo = acTypes.find(a => a.acTypeCode === acType);
                if (acInfo?.crewComplement?.length > 0) return acInfo.crewComplement;

                return [];
            };

            const minCrewCount = 4;  
            const INSTRUCTOR_LINE_TRAINING_CODES = params?.find((item) => item.param === "INSTRUCTOR_LINE_TRAINING_CODES")?.value || 'L, C, A, B, U';
            const INTERNAL_AUDIT_CODES = params?.find((item) => item.param === "INTERNAL_AUDIT_CODES")?.value || 'I, i';
            const UNPAID_CODES = params?.find((item) => item.param === "UNPAID_CODES")?.value || 'O, o';
            const nonDutyCodes = `${INTERNAL_AUDIT_CODES}, ${UNPAID_CODES}`;
            
            filteredFlights.forEach((flight) => {
                const {
                    acType,
                    flightReg,
                    sector,
                    crewMembers,
                } = flight;
                
                if (!Array.isArray(crewMembers)) return;
                                
                const formattedDate = timeMode === "UTC"
                    ? format(new Date(flight.flightDate), "dd/MM/yyyy")
                    : format(new Date(flight.flightDateLocalBase), "dd/MM/yyyy");

                const etd = timeMode === "UTC"
                    ? flight.etd
                    : flight.etdLocalBase;
                                    
                const checkGender = checkType === '' || checkType === 'gender';
                const checkTraining = checkType === '' || checkType === 'training';
                const checkCrewCount = checkType === '' || checkType === 'crewCount';
                const checkComplement = checkType === '' || checkType === 'complement';

                let crewCount = 0;
                let mlCount = 0;
                let fmCount = 0;
                              
                crewMembers.forEach((member) => {
                    if (cabinCrewRanks.includes(member.post) && !member.pax) {
                        if (member.gender === 'ML') mlCount++;
                        if (member.gender === 'FM') fmCount++; 
                        crewCount++;
                    }
                });
                
                if (checkGender) {
                    if (crewCount >= minCrewCount && fmCount <= minFemaleQuantity) {
                        checks.push({
                            flightDate: formattedDate,
                            etd: etd,
                            flightNo: flight.flightNo,
                            sector: flight.sector,
                            error: mlCount >= fmCount 
                                ? `Too many male cabin crew: ${mlCount} ML vs ${fmCount} FL` 
                                : `Too many female cabin crew: ${mlCount} ML vs ${fmCount} FL`
                        });
                    }

                    if (crewCount >= minCrewCount && fmCount >= maxFemaleQuantity) {
                        checks.push({
                            flightDate: formattedDate,
                            etd: etd,
                            flightNo: flight.flightNo,
                            sector: flight.sector,
                            error: `Too many female cabin crew: ${mlCount} ML vs ${fmCount} FL`,
                        });
                    }
                }
                
                  
                let missingTrainingInfo = [];
                const trainerToTraineeMap = { 'L': 'l', 'C': 'c', 'U': 'u', 'B': 'b', 'A': 'a' };
                const traineeToTrainerMap = { 'l': 'L', 'c': 'C', 'u': 'U', 'b': 'B', 'a': 'A' };

                const trnDuties = crewMembers.map(m => m.trnDuty).filter(Boolean);
            
                // Kiểm tra trainer thiếu trainee
                trnDuties.forEach(duty => {
                    if (trainerToTraineeMap[duty]) {
                        const trainee = trainerToTraineeMap[duty];
                        const hasTrainee = trnDuties.includes(trainee);
                        if (!hasTrainee) {
                            missingTrainingInfo.push(`Trainer (${duty}) missing Trainee (${trainee})`);
                        }
                    }
                });

                // Kiểm tra trainee thiếu trainer
                trnDuties.forEach(duty => {
                    if (traineeToTrainerMap[duty]) {
                        const trainer = traineeToTrainerMap[duty];
                        const hasTrainer = trnDuties.includes(trainer);
                        if (!hasTrainer) {
                            missingTrainingInfo.push(`Trainee (${duty}) missing Trainer (${trainer})`);
                        }
                    }
                });
                
                // Training check
                if (checkTraining && missingTrainingInfo.length > 0) {
                    missingTrainingInfo.forEach(msg => {
                        checks.push({
                            flightDate: formattedDate,
                            etd,
                            flightNo: flight.flightNo,
                            sector: flight.sector,
                            error: msg,
                        });
                    });
                }

                // Complement check
                if (acType) {
                    const crewComplement = getCrewComplement(acType, flightReg, sector);
                    if (crewComplement.length > 0) {
                        const crewComplementCount = crewComplement.reduce((sum, { quantity }) => {
                            return sum + (quantity || 0);
                        }, 0);
                        const dutyCrew = crewMembers.filter(
                            member => !member.pax && (!member.trnDuty || !nonDutyCodes.includes(member.trnDuty) || (member.post === 'CM' && !INSTRUCTOR_LINE_TRAINING_CODES.includes(member.trnDuty)))
                        );
                        const dutyCrewCount = dutyCrew.length;

                        if (checkCrewCount) {
                            if (dutyCrewCount > crewComplementCount) {
                                checks.push({
                                    flightDate: formattedDate,
                                    etd,
                                    acType,
                                    flightNo: flight.flightNo,
                                    sector: flight.sector,
                                    error: `Augmented complement: ${dutyCrewCount} > ${crewComplementCount}`,
                                });
                            }
                        }

                        if (checkComplement) {
                            if (selectedACTypes?.length > 0 && !selectedACTypes.includes(acType)) {
                                return;
                            }
                            const postSet = new Set(crewMembers.map(cm => cm.post));
                            crewComplement
                                .filter(({ rankCode }) =>
                                    !selectedRanks?.length || selectedRanks.includes(rankCode)
                                )
                                .forEach(({ rankCode }) => {
                                    if (!postSet.has(rankCode)) {
                                        checks.push({
                                            flightDate: formattedDate,
                                            etd,
                                            flightNo: flight.flightNo,
                                            sector: flight.sector,
                                            error: `Lack post: ${rankCode}`,
                                        });
                                    }
                                });
                        }
                    }
                }
            });
            
            if (checks.length > 0) {
                setChecksList(checks);
                setShowCheckResultDialog(true);
            } else {
                toast.success("No errors found.");
            }
        } catch (error) {
            toast.error(error?.message || "Failed to check errors!");
        }
    }, [
        timeMode, 
        params, 
        filteredFlights, 
        crewRanksData, 
        acTypes, 
        aircrafts, 
        crewComplements,
        checkType, 
        minFemaleQuantity, 
        maxFemaleQuantity,
        selectedRanks,
        selectedACTypes,
    ]);  
      
    const handleExcelJSDownload = () => {
        const exportToExcel = async (data, fileName = "CrewComposition.xlsx") => {
            try {
                const workbook = new ExcelJS.Workbook();
                const worksheet = workbook.addWorksheet("CrewComposition");
        
                // Add headers dynamically based on keys in the first object of the array
                if (data.length > 0) {
                    const headers = ['No.', ...Object.keys(data[0]).filter(key => key !== '_id' && key !== 'key')];
                    worksheet.addRow(headers);
            
                    // Apply header styles
                    headers.forEach((header, index) => {
                        const cell = worksheet.getCell(1, index + 1);
                        cell.font = { bold: true };
                        cell.alignment = { horizontal: "center", vertical: "middle" };
                        cell.fill = {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { argb: "FFFFCC" }, // Light yellow
                        };
                        cell.border = {
                            top: { style: "thin" },
                            left: { style: "thin" },
                            bottom: { style: "thin" },
                            right: { style: "thin" },
                        };
                    });
                }
        
                // Add data rows, replacing '_id' with row number
                data.forEach((row, index) => {
                    const rowData = [index + 1, ...Object.entries(row).filter(([key]) => 
                        key !== '_id' && key !== 'key'
                    ).map(([, value]) => value)];
                    worksheet.addRow(rowData);
                });
        
                // Apply text wrapping to all cells
                worksheet.eachRow((row) => {
                    row.eachCell((cell) => {
                        cell.alignment = { wrapText: true, vertical: "middle", horizontal: "left" };
                        cell.border = {
                            top: { style: "thin" },
                            left: { style: "thin" },
                            bottom: { style: "thin" },
                            right: { style: "thin" },
                        };
                    });
                });
        
                // Adjust column widths based on the content
                worksheet.columns.forEach((column) => {
                column.width = column.header
                    ? Math.max(
                        column.header.length,
                        ...column.values.map((v) => (v ? v.toString().length : 30))
                    ) + 2 
                    : 30;
                });
        
                // Generate and save the Excel file
                const buffer = await workbook.xlsx.writeBuffer();
                saveAs(new Blob([buffer]), fileName);
            } catch (error) {
                toast.error("Failed to export to Excel.", error);
            }
        };
    
        const timestamp = new Date().toISOString().replace(/[-T:.Z]/g, "").slice(0, 14); // YYYYMMDDHHMMSS
        const fileName = `CrewComposition_${timestamp}.xlsx`;
        
        if (!filteredFlights || filteredFlights.length === 0) {
          toast.error("No data available to export.");
          return;
        }
    
        const extractedCrewComposition = filteredFlights.map((route) => {
            const {
                crewMembers = [],
                // flightActualDayDep,
                // flightActualDayArr,
                flightDepDelays,
                flightArrDelays,
                delayRemarks,
                flightStatus,
                blockTime,
                depGate,
                depStand,
                arrGate,
                arrStand,
                expectedPaxPerCategory,
                expectedNoOfPax,
                actualNoOfPax,
                paxPerCategory,
                crewRoutes,
                createdAt,
                updatedAt,
                departureType,
                arrivalType,
                depCountryName,
                arrCountryName,
                fullStd,
                fullSta,
                fullEtd,
                fullEta,
                fullAtd,
                fullAta,
                fullTakeOff,
                fullTouchDown,
                stdLocalBase, 
                staLocalBase, 
                etdLocalBase, 
                etaLocalBase, 
                atdLocalBase, 
                ataLocalBase, 
                doorCloseLocalBase,
                takeOffLocalBase,
                touchDownLocalBase,
                doorClose,
                takeOff,
                touchDown,
                remarks,
                noOfPax,
                flightDateLocalBase,
                flightActualDayDepLocalBase,
                flightActualDayArrLocalBase,
                fullStdLocalBase, 
                fullStaLocalBase, 
                fullEtdLocalBase, 
                fullEtaLocalBase, 
                fullAtdLocalBase, 
                fullAtaLocalBase, 
                fullTakeOffLocalBase,
                fullTouchDownLocalBase,
                prevFlightStatus,
                prevSta,
                prevEta,
                prevAta,
                prevFullSta,
                prevFullEta,
                prevFullAta,
                prevStaLocalBase,
                prevEtaLocalBase,
                prevAtaLocalBase,
                prevFullStaLocalBase,
                prevFullEtaLocalBase,
                prevFullAtaLocalBase,
                capacity,
                loadFactor,
                depDelaysTime,
                depDelaysCode,
                arrDelaysTime,
                arrDelaysCode,
                preArrDelaysTime,
                depTimeDiff,
                arrTimeDiff,
                btDiff,
                scheduleTat,
                estimatedTat,
                actualTat,
                tat,
                tatDiff,
                actualEstimatedTatDiff,
                isGroundTimeRecovery,
                isBlockTimeRecovery,
                taxiOut,
                taxiIn,
                airMinutes,
                blockMinutes,
                blockHours,
                airHours,
                cancelledReason,
                tdDiff,
                taDiff,
                routeCount,
                isSufficientComplement,
                insufficientDetails,
                ...rest
            } = route;
        
            const crewMembersStr = crewMembers
                .map(
                (cm) =>
                    `${cm.crewCode || ""} ${cm.crewName || ""} (${cm.post || ""} | ${cm.base || ""}${cm.pax ? ` | ${cm.pax}` : ""})`
                )
                .join("\n");
        
        
            return {
                ...rest,
                crewMembers: crewMembersStr,
            };
        });
    
        exportToExcel(extractedCrewComposition, fileName);
    };

    const handleFilterOpen = () => setIsFilterDialogOpen(true);
    const handleFilterClose = () => setIsFilterDialogOpen(false);
    
    const handleFilterApply = (filterValues) => {
        setFilters(filterValues); 
    };

    const handleViewClick = useCallback(async (param) => {
        try {      
            const {
                flightDate, 
                flightDesc, 
                dep,
            } = param;

            const {
                flightDetails,
                legMembers,
                jumpSeaters
            } = await fetchLegMembersByFlt(flightDate, flightDesc, dep);
            
            setFlight(flightDetails);
            setCrewList(legMembers);
            setJumpList(jumpSeaters);
            setOpenCrewListDialog(true);
        } catch (error) {
            setOpenCrewListDialog(false);
        } 
    }, []);
    
    const handleCloseCrewListDialog = () => {
        setOpenCrewListDialog(false);
        setFlight([]);
        setCrewList([]);
        setJumpList([]);
    };

    const {
        acTypeOptions,
        flightRegOptions,
        sectorTypeOptions,
        departureTypeOptions,
        arrivalTypeOptions,
    } = useMemo(() => {
        if (!Array.isArray(flights)) {
            return {
                acTypeOptions: [],
                flightRegOptions: [],
                sectorTypeOptions: [],
                departureTypeOptions: [],
                arrivalTypeOptions: [],
            };
        }

        const sorted = (key) =>
            [...new Set(flights.map((f) => f[key]).filter(Boolean))].sort();

        return {
            acTypeOptions: sorted("acType"),
            flightRegOptions: sorted("flightReg"),
            sectorTypeOptions: sorted("sectorType"),
            departureTypeOptions: sorted("departureType"),
            arrivalTypeOptions: sorted("arrivalType"),
        };
    }, [flights]);

    const rankMap = useMemo(() => {
        return new Map(
            crewRanksData?.map(r => [r.rankCode, r]) || []
        );
    }, [crewRanksData]);
    const rankOptions = useMemo(() => {
        if (!Array.isArray(flights)) return [];

        const set = new Set();
        flights.forEach(flight => {
            const sortedCrewMembers = flight.crewMembers
            ?.map(member => {
                const rank = rankMap.get(member?.post);

                return {
                    ...member,
                    rankPriority: rank?.rankPriority ?? Infinity,
                    rankType: rank?.rankType,
                };
            })
            .sort((a, b) => a.rankPriority - b.rankPriority);

            sortedCrewMembers?.forEach(cm => {
                if (cm?.post) set.add(cm.post);
            });
        });

        return [...set];
    }, [flights, rankMap]);


    const columns = useMemo(() => [
        {
            field: "no",
            headerName: "No",
            headerClassName: 'theme-column--header',
            filterable: false,      
            sortable: false,
            renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
            ...(() => {
                return isMobile ? { width: 60 } : { flex: 1 };
            })(),
        },
        {
            field: timeMode === 'UTC' ? "flightDate" : "flightDateLocalBase",
            headerName: "FlightDate",
            headerClassName: 'theme-column--header',
            valueFormatter: (value) => {
                if (value == null) {
                  return '';
                }
                return `${format(value, "dd/MM/yy")}`;
            },
            ...(() => {
                return isGridStretched ? { width: 100 } : { flex: 1.5 };
            })(),
        },
        {
            field: timeMode === 'UTC' ? "flightActualDayDep" : "flightActualDayDepLocalBase",
            headerName: "DepActualDay",
            headerClassName: 'theme-column--header',
            valueFormatter: (value) => {
                if (value == null) {
                  return '';
                }
                return `${format(value, "dd/MM/yy")}`;
            },
            ...(() => {
                return isGridStretched ? { width: 100 } : { flex: 1.5 };
            })(),
        },
        {
            field: timeMode === 'UTC' ? "flightActualDayArr" : "flightActualDayArrLocalBase",
            headerName: "ArrActualDay",
            headerClassName: 'theme-column--header',
            valueFormatter: (value) => {
                if (value == null) {
                  return '';
                }
                return `${format(value, "dd/MM/yy")}`;
            },
            ...(() => {
                return isGridStretched ? { width: 100 } : { flex: 1.5 };
            })(),
        },
        // {
        //     field: "flightCarrier",
        //     headerName: "Carrier",
        //     ...(() => {
        //         return isGridStretched ? { width: 80 } : { flex: 1.2 };
        //     })(),
        // },
        {
            field: "flightDesc",
            headerName: "FltDesc",
            headerClassName: 'theme-column--header',
            cellClassName: "name-column--cell",
            ...(() => {
                return isGridStretched ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        {
            field: "flightNo",
            headerName: "FlightNo",
            headerClassName: 'theme-column--header',
            cellClassName: "name-column--cell",
            ...(() => {
                return isGridStretched ? { width: 80 } : { flex: 1.2 };
            })(),
        },        
        {
            field: "dep",
            headerName: "Dep",
            headerClassName: 'theme-column--header',
            align: "center",
            ...isGridStretched ? { width: 60 } : { flex: 1 },
        },
        {
            field: "arr",
            headerName: "Arr",
            headerClassName: 'theme-column--header',
            align: "center",
            ...isGridStretched ? { width: 60 } : { flex: 1 },
        },
        {
            field: "sector",
            headerName: "Sector",
            headerClassName: 'theme-column--header',
            align: "center",
            ...(() => {
                return isGridStretched ? { width: 100 } : { flex: 1.8 };
            })(),
        },
        {
            field: "acType",
            headerName: "ACType",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isGridStretched ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        {
            field: "flightReg",
            headerName: "Reg",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isGridStretched ? { width: 100 } : { flex: 1.5 };
            })(),
        },
        {
            field: "flightType",
            headerName: "FlightType",
            ...(() => {
                return isGridStretched ? { width: 140 } : { flex: 2 };
            })(),
        },
        // {
        //     field: "sectorType",
        //     headerName: "SectorType",
        //     ...(() => {
        //         return isGridStretched ? { width: 130 } : { flex: 2 };
        //     })(),
        // },  
        {
            field: timeMode === 'UTC' ? "std" : "stdLocalBase",
            headerName: "Std",
            align: "center",
            ...(() => {
                return isGridStretched ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        {
            field: timeMode === 'UTC' ? "sta" : "staLocalBase",
            headerName: "Sta",
            align: "center",
            ...(() => {
                return isGridStretched ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        {
            field: timeMode === 'UTC' ? "etd" : "etdLocalBase",
            headerName: "Etd",
            align: "center",
            ...(() => {
                return isGridStretched ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        {
            field: timeMode === 'UTC' ? "eta" : "etaLocalBase",
            headerName: "Eta",
            align: "center",
            ...(() => {
                return isGridStretched ? { width: 80 } : { flex: 1.2 };
            })(),
        },        
        {
            field: timeMode === 'UTC' ? "doorClose" : "doorCloseLocalBase",
            headerName: "DoorClose",
            headerClassName: 'theme-column--header',
            align: "center",
            ...(() => {
                return isGridStretched ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        {
            field: timeMode === 'UTC' ? "atd" : "atdLocalBase",
            headerName: "Atd",
            headerClassName: 'theme-column--header',
            align: "center",
            ...(() => {
                return isGridStretched ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        {
            field: timeMode === 'UTC' ? "takeOff" : "takeOffLocalBase",
            headerName: "TakeOff",
            headerClassName: 'theme-column--header',
            align: "center",
            ...(() => {
                return isGridStretched ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        {
            field: timeMode === 'UTC' ? "touchDown" : "touchDownLocalBase",
            headerName: "TouchDown",
            headerClassName: 'theme-column--header',
            align: "center",
            ...(() => {
                return isGridStretched ? { width: 80 } : { flex: 1.2 };
            })(),
        }, 
        {
            field: timeMode === 'UTC' ? "ata" : "ataLocalBase",
            headerName: "Ata",
            headerClassName: 'theme-column--header',
            align: "center",
            ...(() => {
                return isGridStretched ? { width: 80 } : { flex: 1.2 };
            })(),
        },        
        {
            field: "tat",
            headerName: "TAT",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isGridStretched ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        {
            field: "scheduleTat",
            headerName: "ScheduleTAT",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isGridStretched ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        {
            field: "estimatedTat",
            headerName: "EstimatedTAT",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isGridStretched ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        {
            field: "tatDiff",
            headerName: "TATDiff",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isGridStretched ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        {
            field: "taxiOut",
            headerName: "TaxiOut",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isGridStretched ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        {
            field: "taxiIn",
            headerName: "TaxiIn",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isGridStretched ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        // {
        //     field: "btDiff",
        //     headerName: "BTDiff",
        //     headerClassName: 'theme-column--header',
        // },
        {
            field: "airMinutes",
            headerName: "AirMinutes",
            headerClassName: 'theme-column--header',
           ...(() => {
                return isGridStretched ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        // {
        //     field: "blockHours",
        //     headerName: "BlockHours",
        //     headerClassName: 'theme-column--header',
        //     valueFormatter: (params) => {
        //         const value = params || 0; 
        //         return parseFloat(value).toFixed(2); 
        //       },
        // },
        {
            field: "blockMinutes",
            headerName: "BlockMinutes",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isGridStretched ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        // {
        //     field: "flightDepDelays",
        //     headerName: "DepDelays",
        //     ...(() => {
        //         return isGridStretched ? { width: 130 } : { flex: 2 };
        //     })(),
        // },
        // {
        //     field: "depDelaysTime",
        //     headerName: "DepDelaysTime",
        //     headerClassName: 'theme-column--header',
        //     ...(() => {
        //         return isGridStretched ? { width: 80 } : { flex: 1.2 };
        //     })(),
        // },
        // {
        //     field: "flightArrDelays",
        //     headerName: "ArrDelays",
        //     ...(() => {
        //         return isGridStretched ? { width: 130 } : { flex: 2 };
        //     })(),
        // },
        // {
        //     field: "arrDelaysTime",
        //     headerName: "ArrDelaysTime",
        //     headerClassName: 'theme-column--header',
        //     ...(() => {
        //         return isGridStretched ? { width: 80 } : { flex: 1.2 };
        //     })(),
        // },              
        {
            field: "flightStatus",
            headerName: "Status",
            ...(() => {
                return isGridStretched ? { width: 100 } : { flex: 1.5 };
            })(),
        },
        {
            field: "remarks",
            headerName: "Remarks",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isGridStretched ? { width: 150 } : { flex: 2 };
            })(),
        },   
        ...(hasCrewMembers ? [
            {
                field: 'crewMembers',
                headerName: 'CrewMembers',
                ...(() => {
                    return isGridStretched ? { width: 333 } : { flex: 6 };
                })(),
                renderCell: (params) => {
                    const sortedCrewMembers = params?.row?.crewMembers
                        ?.map(member => {
                            const rank = crewRanksData.find(rank => rank?.rankCode === member?.post) || {};
                            return { ...member, rankPriority: rank?.rankPriority || Infinity, rankType: rank?.rankType };
                        })
                        .sort((a, b) => a?.rankPriority - b?.rankPriority);

                    return (
                        <Box
                            sx={{
                                height: '100%',
                                alignItems: 'flex-start',
                                overflowY: 'auto',
                                overflowX: 'hidden',
                            }}
                        >
                            {sortedCrewMembers?.map((member, index) => (
                                <Box
                                    key={index}
                                    display="flex"
                                    alignItems="center"
                                    alignContent='flex-start'
                                    gap={1}
                                    sx={{
                                        color: member?.gender === 'FM' 
                                            ? theme.palette.secondary.main 
                                            : theme.palette.primary.dark,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: 'right',
                                            whiteSpace: 'nowrap',
                                            width: '30px',
                                        }}
                                    >
                                        {member.crewCode}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: 'left',
                                        }}
                                    >
                                        {member.crewName}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ textAlign: 'center' }}
                                    >
                                        ({member.post} | {member.base}{member.pax && ` | ${member.pax}`}{member.trnDuty && ` | ${member.trnDuty}`})
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    );
                },
            },
            {
                field: 'crewCount',
                headerName: 'CrewCount',
                ...(isGridStretched ? { width: 90 } : { flex: 1.5 }),
                renderCell: (params) => {
                    const count = params?.row?.crewMembers?.length ?? 0;
                    return (
                        <Box
                            sx={{
                                height: '100%',
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',    // vertical center
                                justifyContent: 'center' // horizontal center
                            }}
                        >
                            <Typography variant="body2">
                                {count}
                            </Typography>
                        </Box>
                    );
                },
            },
            {
                field: "captains",
                headerName: "CP",
                ...isGridStretched ? { width: 250 } : { flex: 4 },
            },
            {
                field: "firstOfficers",
                headerName: "FO",
                ...isGridStretched ? { width: 250 } : { flex: 4 },
            },
            {
                field: "observers",
                headerName: "Obs",
                ...isGridStretched ? { width: 250 } : { flex: 4 },
            },
            {
                field: 'insufficientDetails',
                headerName: 'InsufficientDetails',      
                ...(() => {
                    return isGridStretched ? { width: 160 } : { flex: 2.8 };
                })(),
                renderCell: (params) => {                    
                    return (
                        <Box
                            sx={{
                                height: '100%',
                                alignItems: 'flex-start',
                                overflowY: 'auto',
                                overflowX: 'hidden',
                            }}
                        >
                            {params.row?.insufficientDetails?.map((item, index) => (
                                <Box
                                    key={index}
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    gap={1}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: 'left',
                                            width: '150px',
                                        }}
                                    >
                                        {item?.rankCode}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ textAlign: 'right' }}
                                    >
                                        req {item?.required} | act {item?.assigned}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    );
                },
            },
            {
                field: "isSufficientComplement",
                headerName: "IsSufficientComplement",
                ...(() => {
                    return isGridStretched ? { width: 120 } : { flex: 1.5 };
                })(),
            },
        ] : []),
    ], [timeMode, theme, isMobile, crewRanksData, hasCrewMembers, isGridStretched]);
    
    const actionColumn = useMemo(() => {
        const activeCount = [canView].filter(Boolean).length;
        if (activeCount === 0) return [];
        const baseWidth = 60 + activeCount * 25;

        return [
            {
                field: 'action',
                headerName: 'Action',
                filterable: false,
                sortable: false,
                ...isMobile ? { flex: 1 } : { width: baseWidth },
                renderCell: (params) => (
                    <Box
                        height="100%"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        gap={1}
                    >
                        <Tooltip title="View">
                            <IconButton
                                aria-label="View"
                                sx={{
                                    color: theme.palette.primary.dark,
                                    cursor: 'pointer',
                                    '&:hover': {
                                        color: theme.palette.green.deep,
                                    }
                                }}
                                onClick={() => {
                                    if (!canView) return;
                                    handleViewClick(params.row);
                                }}
                            >
                                <VisibilityIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                ),
            }
        ];
    }, [canView, theme, isMobile, handleViewClick]);
    
    const hiddenColumns = useMemo(() => {
        return {
            _id: false,
            no: false,
            flightDate: false,
            flightDateLocalBase: false,
            flightActualDayArr: false,
            flightActualDayArrLocalBase: false,
            flightDesc: false,
            // acType: false,
            // flightReg: false,
            flightType: false,        
            dep: false,        
            arr: false,    
            std: false,
            sta: false,
            stdLocalBase: false,
            staLocalBase: false,
            doorClose: false,
            doorCloseLocalBase: false,
            // atd: false,
            // ata: false,
            // atdLocalBase: false,
            // ataLocalBase: false,
            takeOff: false,
            touchDown: false,
            takeOffLocalBase: false,
            touchDownLocalBase: false,
            taxiOut: false,
            taxiIn: false,
            scheduleTat: false,
            estimatedTat: false,
            tat: false,
            tatDiff: false,
            airMinutes: false,
            blockMinutes: false,
            flightDepDelays: false,
            depDelaysTime: false,
            flightArrDelays: false,
            arrDelaysTime: false,
            remarks: false,
            sectorType: false,
            flightStatus: false,
            noOfPax: false,
            loadFactor: false,
            crewCount: false,
            captains: false,
            firstOfficers: false,   
            observers: false,
            isSufficientComplement: false,
            insufficientDetails: false,
        }
    }, []);

    const nonMobileHiddenColumns = useMemo(() => {
        return {
            _id: false,
            flightDate: false,
            flightDateLocalBase: false,
            flightActualDayArr: false,
            flightActualDayArrLocalBase: false,
            flightDesc: false,
            flightType: false,
            dep: false,        
            arr: false,    
            std: false,
            sta: false,
            stdLocalBase: false,
            staLocalBase: false,
            doorClose: false,
            doorCloseLocalBase: false,
            // atd: false,
            // ata: false,
            // atdLocalBase: false,
            // ataLocalBase: false,
            taxiOut: false,
            taxiIn: false,
            scheduleTat: false,
            estimatedTat: false,
            tat: false,
            tatDiff: false,
            airMinutes: false,
            blockMinutes: false,
            depDelaysTime: false,
            flightArrDelays: false,
            arrDelaysTime: false,
            remarks: false,
            sectorType: false,
            flightStatus: false,
            noOfPax: false,
            loadFactor: false,   
            crewCount: false,     
            // ...( !isGridStretched
            //     ? { captains: false, firstOfficers: false, observers: false }
            //     : {}
            // ),
            isSufficientComplement: false,
            insufficientDetails: false,
        }
    }, []);

    const gridProps = {
        sx: {
            "& .MuiDataGrid-row.isSufficientComplement-false": {
                backgroundColor: `${theme.palette.primary.light} !important`,  
            },
        },
        
        getRowClassName: ({ row }) => {
            if (row.isSufficientComplement === false) return 'isSufficientComplement-false';
            return '';
        },
    };
        
    const columnsToShow = !canView ? columns : [...columns, ...actionColumn];
    const [columnVisibilityModel, setColumnVisibilityModel] = useState(
        isMobile ? hiddenColumns : nonMobileHiddenColumns
    );

    useEffect(() => {
        setColumnVisibilityModel(isMobile ? hiddenColumns : nonMobileHiddenColumns);
    }, [isMobile, hiddenColumns, nonMobileHiddenColumns]);
    
    
    return (
        <Box className="main-container-box">                 
            <FlexBetween>
                <Header 
                    title="Crew Composition" 
                    subtitle={loading ? "Loading..." : `List of ${filteredFlights?.length || 0} Flights`}
                />
                {!loading && (                    
                    <Box display="flex" alignItems="center" gap={1}>                        
                        {!isMobile && canCheck && (
                            <Box 
                                display="flex" 
                                alignItems="center" 
                                gap={1} 
                            >
                                <FormControlLabel
                                    label="Grid Stretch"
                                    control={
                                        <Switch
                                            checked={isGridStretched}
                                            onChange={() => setIsGridStretched(prev => !prev)}
                                        />
                                    }        
                                /> 
                                {checkType === 'gender' && (
                                    <Box 
                                        display="flex" 
                                        alignItems="center" 
                                        gap={1} 
                                    >
                                        <Tooltip title="Min female cabin crew" arrow>
                                            <span>
                                                <NumberControl
                                                    min={0}
                                                    max={100}
                                                    value={minFemaleQuantity}
                                                    onChange={(value) => {setMinFemaleQuantity(value)}} 
                                                    textFieldWidth={45}
                                                />
                                            </span>
                                        </Tooltip> 

                                        <Tooltip title="Max female cabin crew" arrow>
                                            <span>
                                                <NumberControl
                                                    min={0}
                                                    max={100}
                                                    value={maxFemaleQuantity}
                                                    onChange={(value) => {setMaxFemaleQuantity(value)}} 
                                                    textFieldWidth={45}
                                                />
                                            </span>
                                        </Tooltip>
                                    </Box>                     
                                )}                               
                                {checkType === 'complement' && (
                                    <Box 
                                        display="flex" 
                                        alignItems="center" 
                                        gap={1} 
                                    >           
                                        <FormControl
                                            size="small"   
                                            variant="outlined"            
                                            sx={{ minWidth: { xs: 75, md: 80 } }}  
                                        >
                                            <InputLabel>Rank</InputLabel>
                                            <Select
                                                multiple
                                                label="Rank"
                                                name="selectedRanks"
                                                value={selectedRanks}
                                                onChange={(e) => setSelectedRanks(e.target.value)}
                                            >
                                                {/* <MenuItem key='none' value=''>None</MenuItem> */}
                                                {rankOptions.map((r, index) => (                                    
                                                    <MenuItem key={index} value={r}>
                                                        {r}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        <FormControl
                                            size="small"   
                                            variant="outlined"            
                                            sx={{ minWidth: { xs: 90, md: 100 } }}                                
                                        >
                                            <InputLabel>AC Type</InputLabel>
                                            <Select
                                                multiple
                                                label="AC Type"
                                                name="selectedACTypes"
                                                value={selectedACTypes}
                                                onChange={(e) => setSelectedACTypes(e.target.value)}
                                            >
                                                {acTypeOptions.map((ac, index) => (                                    
                                                    <MenuItem key={index} value={ac}>
                                                        {ac}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Box>                     
                                )}  
                                <FormControl
                                    size="small"                          
                                    variant="outlined"
                                    sx={{ minWidth: { xs: 100, md: 120 } }}
                                >
                                    <InputLabel>Check Type</InputLabel>
                                    <Select
                                        label="Check Type"
                                        name="checkType"
                                        value={checkType}
                                        onChange={(e) => setCheckType(e.target.value)}
                                    >
                                        {checkTypes.map(({ value, label, italic }) => (
                                            <MenuItem
                                                key={value || 'none'}
                                                value={value}
                                                sx={italic ? { fontStyle: 'italic' } : undefined}
                                            >
                                                {label}
                                            </MenuItem>
                                        ))}        
                                    </Select>
                                </FormControl>
                            </Box>              
                        )}
                        
                        {canCheck && (
                            <Tooltip title="Check errors" arrow>
                                <span>
                                    <StyledButton 
                                        onClick={() => {
                                            if (!canCheck) return;
                                            handleCheckErrors();
                                        }}
                                    >
                                        <WarningIcon />
                                    </StyledButton>
                                </span>                            
                            </Tooltip>
                        )}

                        <Tooltip title="Download Excel file" arrow>
                            <span>
                                <StyledButton 
                                    onClick={() => {
                                        // exportToExcel(filteredFlights, "CrewComposition");
                                        handleExcelJSDownload();
                                        logClientAction("EXPORT_REPORT", { fileName: "CrewComposition", format: "Excel", startDate, endDate });
                                    }}
                                >
                                    <DownloadIcon />
                                </StyledButton>
                            </span>              
                        </Tooltip>

                        <FilterButton
                            filters={filters}
                            onClick={handleFilterOpen}
                        />
                    </Box>
                )}                
            </FlexBetween>          
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : (
                <Box
                    display="grid"
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                    gap={2}
                >
                    <Box 
                        ref={scrollRef}
                        height={LAYOUT_HEIGHT} 
                        sx={{ gridColumn: "span 12", overflowY: 'auto' }}
                    >
                        {isMobile ? (
                            <CrewCompositionList 
                                rows={filteredFlights}                                
                                onClick={handleViewClick}
                            />
                        ) : (
                            <DataGridComponent
                                rows={filteredFlights}
                                columns={columnsToShow}
                                columnVisibilityModel={columnVisibilityModel}
                                setColumnVisibilityModel={setColumnVisibilityModel}
                                loading={loading}
                                props={gridProps}
                            />
                        )}

                        <ScrollToTop scrollRef={scrollRef} />
                    </Box>                   
                </Box>
            )}
            
            {showCheckResultDialog && (
                <CheckResultDialog
                    open={showCheckResultDialog}
                    onClose={() => setShowCheckResultDialog(false)}
                    title="Check"
                    checksList={checksList}
                    descriptionKey="error"
                />          
            )}
                
            {isFilterDialogOpen &&
                <FlightFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    acTypeOptions={acTypeOptions} 
                    flightRegOptions={flightRegOptions}
                    sectorTypeOptions={sectorTypeOptions}
                    departureTypeOptions={departureTypeOptions}
                    arrivalTypeOptions={arrivalTypeOptions}
                />
            }
            
            {openCrewListDialog &&
                <CrewListDialog 
                    open={openCrewListDialog}
                    onClose={handleCloseCrewListDialog}
                    flight={flight}
                    crewList={crewList}
                    jumpList={jumpList}
                />
            }     
        </Box>
    );
};

export default CrewComposition;
